/* AgentMeeting — Resolution Center (votes + auto-generated minutes)
 *
 * Wired to the live backend:
 *   - reads localStorage `agentmeeting.currentMeetingId` to pick the active meeting
 *   - GETs meeting record, messages, votes, and existing minutes (404 OK) on mount
 *   - subscribes to the meeting's WebSocket stream and refetches votes when a
 *     `vote.cast` event arrives, or when an `onTurnEnd` event references a
 *     vote-kind message
 *   - host can cast their own vote (approve / approve_with_conditions / block /
 *     abstain) with an optional rationale; on success refetches the tally
 *   - "Generate minutes" calls POST /minutes (LLM-backed, 5+ s); cached minutes
 *     are rendered immediately and a "Regenerate" button overwrites them
 */
import React from 'react';
import { TopBar } from '../shared/topbar';
import { AsciiSep, I } from '../shared/ui';
import {
  resolution as resolutionApi,
  meetings as meetingsApi,
  HTTPError,
  type ServerEvent,
  type MeetingRecord,
} from '../shared/api';
import { useMeetingStream } from '../shared/useMeetingStream';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';

const MEETING_ID_KEY = 'agentmeeting.currentMeetingId';

type Choice = 'approve' | 'approve_with_conditions' | 'block' | 'abstain';

interface VoteRecord {
  choice?: Choice | string;
  rationale?: string;
  participant_id?: string;
  participant_name?: string; // injected by listVotesHandler from participants.display_name
  speaker_name?: string;
  created_at?: string;
  [k: string]: unknown;
}
interface VotesPayload {
  votes: Record<string, VoteRecord>;
  tally: Record<string, number>;
}
interface ServerMsg {
  id: string;
  round_no: number;
  participant_id: string;
  kind: string;
  content: string;
  speaker_name?: string;
  speaker_short?: string;
  speaker_stance?: string;
  speaker_model?: string;
  metadata?: any;
  created_at?: string;
}

const CHOICE_META: Record<Choice, { color: string; label: string; short: string; glyph: string }> = {
  approve:                 { color: 'var(--live)', label: '赞成',     short: '赞成',   glyph: '✓' },
  approve_with_conditions: { color: 'var(--warn)', label: '附条件赞成', short: '附条件', glyph: '~' },
  block:                   { color: 'var(--err)',  label: '反对',     short: '反对',   glyph: '✗' },
  abstain:                 { color: 'var(--fg-3)', label: '弃权',     short: '弃权',   glyph: '○' },
};
const CHOICES: Choice[] = ['approve', 'approve_with_conditions', 'block', 'abstain'];

function emptyTally(): Record<string, number> {
  return { approve: 0, approve_with_conditions: 0, block: 0, abstain: 0 };
}

function goMeetings() {
  if (typeof window !== 'undefined') location.hash = '#/app/meetings';
}

export function ResolutionCenter({ goTo }: { goTo: (route: string) => void }) {
  useAuth();
  const { t } = useI18n();

  const meetingId = typeof window !== 'undefined'
    ? localStorage.getItem(MEETING_ID_KEY)
    : null;

  const [meeting, setMeeting] = React.useState<MeetingRecord | null>(null);
  const [messages, setMessages] = React.useState<ServerMsg[] | null>(null);
  const [votesPayload, setVotesPayload] = React.useState<VotesPayload | null>(null);
  const [minutesMd, setMinutesMd] = React.useState<string>('');
  const [minutesGeneratedAt, setMinutesGeneratedAt] = React.useState<string>('');

  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  // Vote casting form
  const [selectedChoice, setSelectedChoice] = React.useState<Choice | null>(null);
  const [rationale, setRationale] = React.useState<string>('');
  const [castSubmitting, setCastSubmitting] = React.useState<boolean>(false);
  const [castError, setCastError] = React.useState<string | null>(null);

  // Minutes generation
  const [generating, setGenerating] = React.useState<boolean>(false);
  const [minutesError, setMinutesError] = React.useState<string | null>(null);

  // Agent voting
  const [agentVoting, setAgentVoting] = React.useState<boolean>(false);
  const [agentVoteSummary, setAgentVoteSummary] = React.useState<string | null>(null);
  const [agentVoteError, setAgentVoteError] = React.useState<string | null>(null);

  // ── refetch helpers ─────────────────────────────────────
  const refetchVotes = React.useCallback(async () => {
    if (!meetingId) return;
    try {
      const res = await resolutionApi.listVotes(meetingId);
      setVotesPayload({
        votes: res.votes || {},
        tally: { ...emptyTally(), ...(res.tally || {}) },
      });
    } catch (e: unknown) {
      // Non-fatal; keep last good payload.
      // eslint-disable-next-line no-console
      console.warn('listVotes failed', e);
    }
  }, [meetingId]);

  // ── initial fetch ────────────────────────────────────────
  React.useEffect(() => {
    if (!meetingId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      meetingsApi.get(meetingId).catch((e: unknown) => {
        if (!cancelled) setLoadErr(e instanceof HTTPError ? `meeting ${e.status}` : `${e}`);
        return null;
      }),
      meetingsApi.messages(meetingId)
        .then(r => (r.messages || []) as ServerMsg[])
        .catch((e: unknown) => {
          if (!cancelled) setLoadErr(e instanceof HTTPError ? `messages ${e.status}` : `${e}`);
          return [] as ServerMsg[];
        }),
      resolutionApi.listVotes(meetingId)
        .then(r => ({ votes: r.votes || {}, tally: { ...emptyTally(), ...(r.tally || {}) } }))
        .catch((e: unknown) => {
          if (!cancelled) setLoadErr(e instanceof HTTPError ? `votes ${e.status}` : `${e}`);
          return { votes: {}, tally: emptyTally() } as VotesPayload;
        }),
      // 404 here is expected — minutes have not been generated yet.
      resolutionApi.getMinutes(meetingId)
        .then(r => ({ minutes_md: r.minutes_md || '', generated_at: r.generated_at || '' }))
        .catch((e: unknown) => {
          if (e instanceof HTTPError && e.status === 404) {
            return { minutes_md: '', generated_at: '' };
          }
          if (!cancelled) setLoadErr(e instanceof HTTPError ? `minutes ${e.status}` : `${e}`);
          return { minutes_md: '', generated_at: '' };
        }),
    ]).then(([mt, msgs, vp, mins]) => {
      if (cancelled) return;
      setMeeting(mt);
      setMessages(msgs);
      setVotesPayload(vp);
      setMinutesMd(mins.minutes_md);
      setMinutesGeneratedAt(mins.generated_at);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [meetingId]);

  // ── live-stream subscription: refetch votes on relevant events ───
  // Single subscription; the named `onTurnEnd` callback catches vote-kind
  // messages, and the `events` array (which keeps `vote.cast` in its raw
  // form because the hook routes that type through to `default`) is scanned
  // for any newly arrived vote.cast frames.
  const stream = useMeetingStream(meetingId, {
    onTurnEnd: (ev: ServerEvent) => {
      const kind = ev?.payload?.kind || ev?.payload?.message?.kind;
      if (kind === 'vote') void refetchVotes();
    },
  });
  const lastVoteCastSeenRef = React.useRef<number>(0);
  React.useEffect(() => {
    if (!stream.events.length) return;
    for (let i = lastVoteCastSeenRef.current; i < stream.events.length; i++) {
      if (stream.events[i].type === 'vote.cast') {
        void refetchVotes();
        break;
      }
    }
    lastVoteCastSeenRef.current = stream.events.length;
  }, [stream.events, refetchVotes]);

  // ── empty state ──────────────────────────────────────────
  if (!meetingId) {
    return (
      <div className="frame col" style={{ height: '100%' }}>
        <TopBar here="resolution" goTo={goTo} role="host" />
        <div className="grow flex center" style={{ justifyContent: 'center' }}>
          <div className="col gap-3" style={{
            maxWidth: 420, padding: 24, border: '1px solid var(--line-2)',
            borderRadius: 6, background: 'var(--bg-1)', textAlign: 'center',
          }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NO MEETING SELECTED</div>
            <div style={{ fontSize: 15 }}>Pick a meeting to view its resolution.</div>
            <button className="btn primary" onClick={goMeetings}>Open meetings →</button>
          </div>
        </div>
      </div>
    );
  }

  // ── loading state ────────────────────────────────────────
  if (loading || votesPayload == null) {
    return (
      <div className="frame col" style={{ height: '100%' }}>
        <TopBar here="resolution" goTo={goTo} role="host" />
        <div className="grow flex center" style={{ justifyContent: 'center' }}>
          <div className="col gap-2" style={{ alignItems: 'center' }}>
            <div className="mono tiny dim">loading resolution…</div>
            {loadErr && (
              <div className="mono tiny" style={{ color: 'var(--err)' }}>{loadErr}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const tally = votesPayload.tally;
  const votes = votesPayload.votes;
  const totalCast =
    (tally.approve || 0) +
    (tally.approve_with_conditions || 0) +
    (tally.block || 0) +
    (tally.abstain || 0);
  const sameDir = (tally.approve || 0) + (tally.approve_with_conditions || 0);

  const onCastVote = async () => {
    if (!selectedChoice || !meetingId) return;
    setCastError(null);
    setCastSubmitting(true);
    try {
      await resolutionApi.castVote(meetingId, {
        choice: selectedChoice,
        rationale: rationale.trim() || undefined,
      });
      setRationale('');
      setSelectedChoice(null);
      await refetchVotes();
    } catch (e: unknown) {
      setCastError(
        e instanceof HTTPError
          ? `vote failed (HTTP ${e.status}${e.body?.error ? ` · ${e.body.error}` : ''})`
          : `${e}`,
      );
    } finally {
      setCastSubmitting(false);
    }
  };

  const onAgentVote = async () => {
    if (!meetingId) return;
    setAgentVoteError(null);
    setAgentVoteSummary(null);
    setAgentVoting(true);
    try {
      const res = await resolutionApi.castAgentVotes(meetingId);
      const errs = res.errors?.length || 0;
      setAgentVoteSummary(
        `${res.votes_cast} agent 投票完成` + (errs ? ` · ${errs} 个失败` : '')
      );
      await refetchVotes();
    } catch (e: unknown) {
      setAgentVoteError(
        e instanceof HTTPError
          ? `agent vote failed (HTTP ${e.status}${e.body?.error ? ` · ${e.body.error}` : ''})`
          : `${e}`,
      );
    } finally {
      setAgentVoting(false);
    }
  };

  const onGenerateMinutes = async () => {
    if (!meetingId) return;
    setMinutesError(null);
    setGenerating(true);
    try {
      const res = await resolutionApi.generateMinutes(meetingId);
      setMinutesMd(res.minutes_md || '');
      setMinutesGeneratedAt(new Date().toISOString());
    } catch (e: unknown) {
      setMinutesError(
        e instanceof HTTPError
          ? `generate failed (HTTP ${e.status}${e.body?.error ? ` · ${e.body.error}` : ''})`
          : `${e}`,
      );
    } finally {
      setGenerating(false);
    }
  };

  const title = meeting?.title || meeting?.topic || `meeting ${meetingId.slice(0, 8)}`;

  return (
    <div className="frame" style={{ height: '100%' }}>
      <TopBar here="resolution" goTo={goTo} role="host" />

      {/* meta */}
      <div className="flex center" style={{ padding: '14px 22px 12px', gap: 18, borderBottom: '1px solid var(--line-1)' }}>
        <div className="col gap-1 grow">
          <div className="meta">
            <span className="dim">[DECISION]</span> {meetingId.slice(0, 8)} · resolution
          </div>
          <div style={{ fontSize: 17, fontWeight: 500, letterSpacing: '-0.01em' }}>
            {title}
          </div>
          <div className="meta dim">
            cast {totalCast} · same-direction {sameDir} ·
            {minutesMd
              ? <span className="live-fg"> minutes ready</span>
              : <span className="warn-fg"> minutes not generated</span>}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            className="btn"
            disabled={agentVoting}
            onClick={onAgentVote}
            title="让 agent 也按各自立场投票"
          >
            {agentVoting ? '$ agents voting…' : '$ 让 agent 投票'}
          </button>
          {!minutesMd ? (
            <button
              className="btn primary"
              disabled={generating}
              onClick={onGenerateMinutes}
              title={t('app.resolution.minutes')}
            >
              {I.play} {generating ? 'generating…' : 'Generate minutes'}
            </button>
          ) : (
            <button
              className="btn"
              disabled={generating}
              onClick={onGenerateMinutes}
              title={t('app.resolution.minutes')}
            >
              {generating ? 'regenerating…' : '↺ Regenerate'}
            </button>
          )}
        </div>
      </div>

      {(agentVoteSummary || agentVoteError) && (
        <div className="meta" style={{
          padding: '8px 22px',
          borderBottom: '1px solid var(--line-1)',
          fontSize: 12,
          color: agentVoteError ? 'var(--err)' : 'var(--live)',
        }}>
          {agentVoteError ? `! ${agentVoteError}` : `✓ ${agentVoteSummary}`}
        </div>
      )}

      <div className="grow flex" style={{ minHeight: 0 }}>
        <div className="col grow scroll" style={{ minWidth: 0 }}>
          {/* Cast vote panel */}
          <CastVotePanel
            selectedChoice={selectedChoice}
            setSelectedChoice={setSelectedChoice}
            rationale={rationale}
            setRationale={setRationale}
            onSubmit={onCastVote}
            submitting={castSubmitting}
            error={castError}
          />

          {/* Live tally */}
          <AsciiSep label={t('app.resolution.tally') + ' · live'} total={70} />
          <TallyView tally={tally} totalCast={totalCast} />

          {/* Per-vote breakdown */}
          <AsciiSep label={t('app.resolution.vote')} total={70} />
          <VoteBreakdown votes={votes} />

          {/* Minutes */}
          <AsciiSep label={t('app.resolution.minutes') + '.md'} total={70} />
          <MinutesView
            md={minutesMd}
            generatedAt={minutesGeneratedAt}
            generating={generating}
            error={minutesError}
            onGenerate={onGenerateMinutes}
          />

          <div style={{ height: 32 }} />
        </div>

        {/* right: stream status */}
        <div className="vr" />
        <div className="col" style={{ width: 280, flex: '0 0 280px', background: 'var(--bg-1)' }}>
          <div className="flex between center" style={{ padding: '12px 14px', borderBottom: '1px solid var(--line-1)' }}>
            <span className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>STREAM</span>
            <span className="mono tiny" style={{ color: stream.connected ? 'var(--live)' : 'var(--fg-3)' }}>
              {stream.connected ? '● live' : '○ idle'}
            </span>
          </div>
          <div className="col gap-2" style={{ padding: 14 }}>
            <div className="meta dim">Live updates refresh the tally automatically.</div>
            {stream.lastError && (
              <div className="mono tiny" style={{ color: 'var(--err)' }}>{stream.lastError}</div>
            )}
            {messages != null && (
              <div className="meta dim">{messages.length} message{messages.length === 1 ? '' : 's'} in transcript</div>
            )}
          </div>

          <div className="grow" />

          <div className="col gap-2" style={{ padding: 14, borderTop: '1px solid var(--line-1)' }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NEXT</div>
            <button className="btn ghost" onClick={() => goTo('replay')} style={{ width: '100%', justifyContent: 'space-between' }}>
              fork & re-run {I.chevR}
            </button>
            <button className="btn ghost" onClick={() => goTo('meetings')} style={{ width: '100%', justifyContent: 'space-between' }}>
              back to meetings {I.chevR}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── sub-components ──────────────────────────────────────

function CastVotePanel({
  selectedChoice, setSelectedChoice,
  rationale, setRationale,
  onSubmit, submitting, error,
}: {
  selectedChoice: Choice | null;
  setSelectedChoice: (c: Choice | null) => void;
  rationale: string;
  setRationale: (s: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const { t } = useI18n();
  return (
    <div className="col gap-3" style={{ padding: '14px 22px 6px' }}>
      <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.resolution.ballot')}</div>

      <div className="flex gap-2 wrap">
        {CHOICES.map(c => {
          const m = CHOICE_META[c];
          const active = selectedChoice === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setSelectedChoice(active ? null : c)}
              className="btn"
              disabled={submitting}
              style={{
                padding: '8px 14px',
                borderColor: active ? m.color : 'var(--line-2)',
                background: active ? m.color + '20' : 'var(--bg-2)',
                color: active ? m.color : 'var(--fg-1)',
              }}
            >
              <span style={{ fontFamily: 'var(--mono)' }}>{m.glyph}</span> {m.label}
            </button>
          );
        })}
      </div>

      <textarea
        value={rationale}
        onChange={e => setRationale(e.target.value)}
        placeholder="Rationale (optional) — why this choice?"
        rows={3}
        disabled={submitting}
        style={{
          width: '100%',
          padding: 10,
          fontFamily: 'inherit',
          fontSize: 13,
          color: 'var(--fg-0)',
          background: 'var(--bg-1)',
          border: '1px solid var(--line-2)',
          borderRadius: 4,
          resize: 'vertical',
        }}
      />

      <div className="flex center gap-2">
        <button
          className="btn primary"
          disabled={!selectedChoice || submitting}
          onClick={onSubmit}
        >
          {I.vote} {submitting ? 'casting…' : t('app.resolution.vote')}
        </button>
        {error && <span className="mono tiny" style={{ color: 'var(--err)' }}>{error}</span>}
      </div>
    </div>
  );
}

function TallyView({ tally, totalCast }: { tally: Record<string, number>; totalCast: number }) {
  if (totalCast === 0) {
    return (
      <div className="col" style={{ padding: '6px 22px 16px' }}>
        <div className="meta dim" style={{
          padding: '14px',
          border: '1px dashed var(--line-2)',
          borderRadius: 4,
          textAlign: 'center',
        }}>
          No votes cast yet.
        </div>
      </div>
    );
  }
  const segments: { id: Choice; val: number; color: string; label: string }[] = CHOICES.map(c => ({
    id: c,
    val: tally[c] || 0,
    color: CHOICE_META[c].color,
    label: CHOICE_META[c].label,
  }));
  const sameDir = (tally.approve || 0) + (tally.approve_with_conditions || 0);

  return (
    <div className="col gap-3" style={{ padding: '6px 22px 16px' }}>
      <div className="flex" style={{ height: 28, border: '1px solid var(--line-2)', borderRadius: 3, overflow: 'hidden' }}>
        {segments.map(s => s.val > 0 && (
          <div key={s.id} title={`${s.label}: ${s.val}`} style={{
            flex: s.val, background: s.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--bg-0)',
            fontWeight: 600,
            transition: 'flex .4s ease',
          }}>
            {s.val}
          </div>
        ))}
      </div>
      <div className="flex wrap gap-3 mono sm">
        {segments.map(s => (
          <span key={s.id} className="flex center gap-2">
            <span className="dot" style={{ background: s.color, width: 10, height: 10 }} />
            <span className="dim">{s.label}</span>
            <span className="tnum" style={{ color: s.color }}>{s.val}</span>
          </span>
        ))}
        <span className="flex center gap-2" style={{ marginLeft: 'auto' }}>
          <span className="dim">同向小计 (approve + conditional)</span>
          <span className="tnum live-fg">{sameDir}</span>
          <span className="dim">·</span>
          <span className="dim">total cast</span>
          <span className="tnum">{totalCast}</span>
        </span>
      </div>
    </div>
  );
}

function VoteBreakdown({ votes }: { votes: Record<string, VoteRecord> }) {
  const entries = Object.entries(votes);
  if (entries.length === 0) {
    return (
      <div className="col" style={{ padding: '6px 22px 16px' }}>
        <div className="meta dim" style={{
          padding: '14px',
          border: '1px dashed var(--line-2)',
          borderRadius: 4,
          textAlign: 'center',
        }}>
          No votes cast yet.
        </div>
      </div>
    );
  }
  return (
    <div className="col" style={{ padding: '6px 22px 16px' }}>
      <div className="col" style={{ border: '1px solid var(--line-1)', borderRadius: 4, overflow: 'hidden' }}>
        {entries.map(([key, v], i) => {
          const choice = (v.choice as Choice) || 'abstain';
          const meta = CHOICE_META[choice] || CHOICE_META.abstain;
          const name = v.participant_name || v.speaker_name || key.slice(0, 8) + '…';
          const rationale = (v.rationale as string) || '';
          return (
            <div key={key} className="flex" style={{
              padding: '10px 14px', gap: 12, alignItems: 'flex-start',
              borderBottom: i < entries.length - 1 ? '1px solid var(--line-1)' : 'none',
            }}>
              <span className="mono tiny dim" style={{ width: 18, paddingTop: 4 }}>#{i + 1}</span>
              <div className="col grow gap-1" style={{ minWidth: 0 }}>
                <div className="flex center gap-2">
                  <span className="sm" style={{ fontWeight: 500 }}>{name}</span>
                </div>
                {rationale && (
                  <div className="meta" style={{ color: 'var(--fg-1)', whiteSpace: 'pre-wrap' }}>
                    {rationale}
                  </div>
                )}
              </div>
              <div className="flex center gap-2" style={{
                padding: '4px 10px',
                border: '1px solid ' + meta.color,
                background: meta.color + '15',
                borderRadius: 4,
                minWidth: 110, justifyContent: 'center',
              }}>
                <span className="mono" style={{ color: meta.color, fontSize: 14 }}>{meta.glyph}</span>
                <span className="mono sm" style={{ color: meta.color, letterSpacing: '0.04em' }}>{meta.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MinutesView({
  md, generatedAt, generating, error, onGenerate,
}: {
  md: string;
  generatedAt: string;
  generating: boolean;
  error: string | null;
  onGenerate: () => void;
}) {
  if (generating && !md) {
    return (
      <div className="col" style={{ padding: '14px 22px' }}>
        <div style={{
          padding: 24, border: '1px dashed var(--line-2)', borderRadius: 4,
          textAlign: 'center',
        }}>
          <div className="mono tiny dim">generating minutes…</div>
          <div className="meta dim" style={{ marginTop: 6 }}>
            calling LLM, this may take 5+ seconds
          </div>
        </div>
      </div>
    );
  }
  if (!md) {
    return (
      <div className="col" style={{ padding: '14px 22px' }}>
        <div style={{
          padding: 18, border: '1px dashed var(--line-2)', borderRadius: 4,
          textAlign: 'center',
        }}>
          <div className="meta dim">No minutes yet.</div>
          <div className="flex center gap-2" style={{ justifyContent: 'center', marginTop: 10 }}>
            <button className="btn primary" disabled={generating} onClick={onGenerate}>
              {I.play} {generating ? 'generating…' : 'Generate minutes'}
            </button>
          </div>
          {error && <div className="mono tiny" style={{ color: 'var(--err)', marginTop: 8 }}>{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="col" style={{ padding: '14px 22px' }}>
      <div style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--line-2)',
        borderRadius: 4,
        padding: 18,
        fontFamily: 'var(--mono)',
        fontSize: 12.5,
        lineHeight: 1.7,
        color: 'var(--fg-1)',
      }}>
        <Markdown md={md} />
      </div>
      <div className="flex center gap-2" style={{ padding: '10px 0 0', color: 'var(--fg-3)' }}>
        <span className="mono tiny">
          {generatedAt ? `generated at ${generatedAt}` : 'minutes (cached)'}
        </span>
        <span className="mono tiny">·</span>
        <span className="mono tiny">summarizer · LLM-backed</span>
        {error && <span className="mono tiny" style={{ color: 'var(--err)', marginLeft: 8 }}>{error}</span>}
        <button
          className="btn ghost"
          style={{ marginLeft: 'auto' }}
          disabled={generating}
          onClick={onGenerate}
        >
          {generating ? 'regenerating…' : '↺ Regenerate'}
        </button>
      </div>
    </div>
  );
}

/**
 * Minimal markdown renderer — splits on \n and emits headings, bullets,
 * blockquotes, and paragraphs. No external dependency.
 */
function Markdown({ md }: { md: string }) {
  const lines = md.split('\n');
  const out: React.ReactNode[] = [];
  let buf: string[] = [];
  let bulletBuf: string[] = [];
  const flushPara = () => {
    if (buf.length === 0) return;
    out.push(
      <p key={`p-${out.length}`} style={{ margin: '0 0 10px', whiteSpace: 'pre-wrap' }}>
        {buf.join('\n')}
      </p>,
    );
    buf = [];
  };
  const flushBullets = () => {
    if (bulletBuf.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} style={{ margin: '0 0 10px 18px', padding: 0 }}>
        {bulletBuf.map((b, i) => (
          <li key={i} style={{ marginBottom: 4 }}>{b}</li>
        ))}
      </ul>,
    );
    bulletBuf = [];
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line.startsWith('### ')) {
      flushPara(); flushBullets();
      out.push(
        <h3 key={`h3-${out.length}`} style={{ fontSize: 13, margin: '12px 0 6px', color: 'var(--fg-0)' }}>
          {line.slice(4)}
        </h3>,
      );
    } else if (line.startsWith('## ')) {
      flushPara(); flushBullets();
      out.push(
        <h2 key={`h2-${out.length}`} style={{ fontSize: 14, margin: '14px 0 6px', color: 'var(--fg-0)' }}>
          {line.slice(3)}
        </h2>,
      );
    } else if (line.startsWith('# ')) {
      flushPara(); flushBullets();
      out.push(
        <h1 key={`h1-${out.length}`} style={{ fontSize: 16, margin: '16px 0 8px', color: 'var(--fg-0)' }}>
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      flushPara();
      bulletBuf.push(line.slice(2));
    } else if (line.trim() === '') {
      flushPara();
      flushBullets();
    } else {
      flushBullets();
      buf.push(line);
    }
  }
  flushPara();
  flushBullets();

  return <>{out}</>;
}
